/**
 * Company-wide acquisition targets rendered on the admin Signup Funnel tab.
 *
 * Team-wide target set in the 2026-08-17 weekly sync: 3-4 Stripe trials/day
 * (~120/month) converting at 20% to paid (~24 paying customers/month), gated on
 * an activation floor of 30% of signups launching a test. Baseline at the time
 * was ~1 trial/day with 6.3% activation, which is why the activation ratio is
 * rendered beside the trial count and never on its own: trial volume climbing
 * without activation reads as progress in a funnel that does not convert.
 *
 * The activation floor is deliberately far above the baseline — it is the bar the
 * product has to clear, not a description of where we are, so expect activation to
 * grade critical until it moves. Do not soften the gate to make the gauge green.
 */

/** Trials/day at the bottom of the 3-4/day band — the pace line on the sparkline. */
export const TRIAL_TARGET_PER_DAY = 4

/** Full-month trial target. Prorated by window length for shorter ranges. */
const TRIAL_TARGET_PER_MONTH = 120

/** Days used to prorate the monthly target onto an arbitrary window. */
const TRIAL_TARGET_MONTH_DAYS = 30

/**
 * Activation floor: share of completed signups that launch a test. An aspirational
 * bar (see the file header), not the current baseline.
 */
export const ACTIVATION_GATE = 0.3

/** Share of trials expected to convert to paid. */
export const TRIAL_TO_PAID_TARGET = 0.2

/** Prorate the monthly trial target onto a window of `days` days. */
export function prorateTrialTarget(days: number): number {
  if (!Number.isFinite(days) || days <= 0) return TRIAL_TARGET_PER_MONTH
  return Math.round((TRIAL_TARGET_PER_MONTH / TRIAL_TARGET_MONTH_DAYS) * days)
}

/** Share of the goal below which a metric reads as off-track rather than merely behind. */
const GOAL_CRITICAL_RATIO = 0.5

export type GoalTier = 'onTrack' | 'behind' | 'critical'

/**
 * Grade `ratio` (achieved / goal, where 1 means the goal is met).
 * On track at or above the goal, critical below half of it, behind in between.
 */
export function goalTier(ratio: number | null | undefined): GoalTier | null {
  if (ratio == null || !Number.isFinite(ratio)) return null
  if (ratio >= 1) return 'onTrack'
  if (ratio < GOAL_CRITICAL_RATIO) return 'critical'
  return 'behind'
}
